<template>
  <v-row dense>
    <v-col cols="12" md="6">
      <v-text-field
        @update:model-value="setName"
        :model-value="name ? String(name) : undefined"
        class="w-full"
        label="Nome do Grupo"
        color="primary20"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
      />
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        @update:model-value="setEmail"
        :model-value="email ? String(email) : undefined"
        class="w-full"
        label="Email"
        color="primary20"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
      />
    </v-col>

    <v-col cols="12" class="mt-4">
      <v-autocomplete
        :disabled="isUpdateGroup"
        @update:model-value="setCompany"
        :model-value="company ? String(company) : undefined"
        :items="listCompanies"
        class="w-full"
        color="primary20"
        label="* Empresa"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
      />
    </v-col>

    <v-col cols="12">
      <v-divider class="my-2"></v-divider>
      <div class="flex items-center justify-between mb-2">
        <strong class="text-sm font-bold text-gray-700">Participantes do Grupo</strong>
        <span class="text-xs text-gray-500">{{ users?.length || 0 }} selecionado(s)</span>
      </div>
    </v-col>

    <v-col cols="12">
      <v-autocomplete
        v-model="userToAdd"
        :items="availableUsers"
        label="Adicionar pessoas ao grupo"
        color="primary20"
        variant="outlined"
        density="compact"
        hide-details
        clear-on-select
        placeholder="Pesquise por nome ou email"
        no-data-text="Nenhum usuário encontrado ou todos já selecionados"
        return-object
        @update:model-value="handleAddUser"
      >
        <template v-slot:prepend-inner>
          <v-icon :icon="mdiMagnify" color="gray" />
        </template>

        <template v-slot:item="{ props, item }">
          <v-list-item
            v-bind="props"
            :prepend-icon="mdiAccount"
            :title="item.raw.title"
            :subtitle="item.raw.subtitle"
          />
        </template>
      </v-autocomplete>
    </v-col>

    <v-col cols="12" class="mt-2">
      <div class="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
        <div
          v-if="!users || users.length === 0"
          class="py-8 text-center text-gray-400 text-sm flex flex-col items-center"
        >
          <v-icon :icon="mdiAccountMultiple" size="large" class="mb-2 opacity-50" />
          Nenhum participante adicionado ainda.
        </div>

        <div v-else class="ds-scrollbar max-h-[300px] overflow-y-auto">
          <div
            v-for="user in selectedUsersDetails"
            :key="user.value"
            class="flex items-center justify-between p-3 border-b border-gray-100 last:border-0 bg-white hover:bg-gray-50 transition-colors"
          >
            <div class="flex items-center gap-3 overflow-hidden">
              <v-avatar color="primary30" size="36">
                <span class="text-xs font-bold text-white">{{ getInitials(user.title) }}</span>
              </v-avatar>

              <div class="flex flex-col text-left truncate">
                <span class="text-sm font-medium text-gray-800 truncate" :title="user.title">
                  {{ user.title }}
                </span>
                <span class="text-xs text-gray-500 truncate" :title="user.subtitle">
                  {{ user.subtitle }}
                </span>
              </div>
            </div>

            <v-btn
              icon
              variant="text"
              density="comfortable"
              color="red-lighten-1"
              @click="handleRemoveUser(user.value)"
              title="Remover usuário"
            >
              <v-icon :icon="mdiTrashCanOutline" size="small" />
            </v-btn>
          </div>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useUserStore } from '@/pinia-store/useUsersStore'
import { storeToRefs } from 'pinia'
import { onMounted, ref, computed } from 'vue'
import { mdiMagnify, mdiAccount, mdiTrashCanOutline, mdiAccountMultiple } from '@mdi/js'
import { useErrorToast } from '@/composables/useErrorToast'

const props = defineProps(['name', 'email', 'company', 'users', 'isUpdateGroup'])
const emit = defineEmits(['update:name', 'update:email', 'update:company', 'update:users'])

const setName = (v: string) => emit('update:name', v)
const setEmail = (v: string) => emit('update:email', v)
const setCompany = (v: string) => emit('update:company', v)

const userStore = useUserStore()
const companyStore = useCompanyStore()
const { notifyError } = useErrorToast()
const { listUsers } = storeToRefs(userStore)
const { listCompanies } = storeToRefs(companyStore)

const userToAdd = ref(null)

const availableUsers = computed(() => {
  const currentIds = props.users || []
  return listUsers.value.filter((u) => !currentIds.includes(u.value))
})

const selectedUsersDetails = computed(() => {
  const currentIds = props.users || []
  return currentIds
    .map((id: string) => listUsers.value.find((u) => u.value === id))
    .filter((u: any) => !!u)
})

const handleAddUser = (item: any) => {
  if (!item) return
  const currentUsers = props.users ? [...props.users] : []
  if (!currentUsers.includes(item.value)) {
    currentUsers.push(item.value)
    emit('update:users', currentUsers)
  }
  userToAdd.value = null
}

const handleRemoveUser = (userId: string) => {
  const currentUsers = props.users ? [...props.users] : []
  const newUsers = currentUsers.filter((id: string) => id !== userId)
  emit('update:users', newUsers)
}

const getInitials = (name: string) => {
  if (!name) return 'U'
  return name.substring(0, 2).toUpperCase()
}

onMounted(async () => {
  try {
    if (userStore.fetchUsers) {
      await userStore.fetchUsers()
    } else {
      await (userStore as any).getAllUsers()
    }
  } catch (error) {
    // Sem a lista, o seletor de membros fica vazio — avisa e segue carregando as empresas.
    notifyError(error, 'Não foi possível carregar os usuários para adicionar ao grupo.')
  }

  await companyStore.getMyCompanies()
})
</script>
